import { Form, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyGlobalTextSourceToItems,
  applyUploadItemFormValues,
  buildPreviewMetadataFormData,
  buildUploadFormDataForItem,
  buildUploadItemFormValues,
  buildUploadItemsFromFiles,
  mergePreviewResult,
  resolveDefaultPlatformsForFiles,
} from '../contentListUploadFlow';

export const useContentListUpload = ({
  navigate,
  noActivePlan,
  storageActionBlocked,
  resolveErrorMessage,
  sanitizeDeliveryPlatformsByValueSet,
  isVideoFileName,
  sameArray,
  defaultVideoPlatforms,
  previewContentMetadata,
  uploadContent,
  loadData,
  refreshQuota,
}) => {
  const [uploadForm] = Form.useForm();
  const [uploadItemForm] = Form.useForm();

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadItems, setUploadItems] = useState([]);
  const [selectedUploadUid, setSelectedUploadUid] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewingAction, setPreviewingAction] = useState(null); // 'title' | 'all' | null
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ total: 0, done: 0, ok: 0, failed: 0 });
  const [batchFailures, setBatchFailures] = useState([]);
  const [batchSkipDone, setBatchSkipDone] = useState(true);
  const [globalTextSource, setGlobalTextSource] = useState('asr'); // asr|image|auto
  const [globalOcrMaxFrames, setGlobalOcrMaxFrames] = useState(6);
  const [uploading, setUploading] = useState(false);
  const suppressUploadItemSyncRef = useRef(false);

  const openUploadModal = () => setUploadModalVisible(true);

  const closeUploadModal = () => {
    setUploadModalVisible(false);
    uploadForm.resetFields();
    setFileList([]);
    setUploadItems([]);
    setSelectedUploadUid(null);
    uploadItemForm.resetFields();
    setPreviewing(false);
    setPreviewingAction(null);
    setBatchGenerating(false);
    setBatchProgress({ total: 0, done: 0, ok: 0, failed: 0 });
    setBatchFailures([]);
  };

  const handleUpload = async () => {
    if (storageActionBlocked) {
      const text = noActivePlan
        ? '当前订阅未生效，请先购买或续费。'
        : '存储空间额度已用完，请前往「付费订阅」升级套餐。';
      message.warning(`${text} 正在前往付费订阅页面...`);
      navigate('/billing');
      return;
    }
    if (fileList.length === 0) {
      message.warning('请至少选择一个文件');
      return;
    }

    try {
      const values = await uploadForm.validateFields();
      setUploading(true);

      const items = uploadItems || [];
      if (items.length === 0) {
        message.warning('请至少选择一个文件');
        return;
      }

      let okCount = 0;
      const failed = [];

      for (const it of items) {
        const f = it?.file?.originFileObj || it?.file;
        if (!f) continue;
        const formData = buildUploadFormDataForItem({
          values,
          item: it,
          file: f,
          sanitizeDeliveryPlatforms: sanitizeDeliveryPlatformsByValueSet,
          isVideoFileName,
          defaultVideoPlatforms,
        });

        try {
          const resp = await uploadContent(formData);
          if (resp?.status === 'success') {
            okCount += 1;
          } else {
            failed.push({ name: it.name, error: resolveErrorMessage(resp?.message, '上传失败') });
          }
        } catch (e) {
          failed.push({ name: it.name, error: e?.message || String(e) });
        }
      }

      if (failed.length === 0) {
        message.success(`成功上传 ${okCount} 个文件`);
        closeUploadModal();
        await loadData();
        refreshQuota().catch(() => {});
      } else {
        message.warning(`上传完成：成功 ${okCount}，失败 ${failed.length}（请查看控制台详情）`);
        // eslint-disable-next-line no-console
        console.error('上传失败列表:', failed);
        await loadData();
        refreshQuota().catch(() => {});
      }
    } catch (error) {
      console.error('上传失败:', error);
      if (error.errorFields) return;
      message.error('上传失败，请稍后重试');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);

    try {
      const currentPlatforms = uploadForm.getFieldValue('available_platforms');
      const nextPlatforms = resolveDefaultPlatformsForFiles({
        currentPlatforms,
        newFileList,
        sanitizeDeliveryPlatforms: sanitizeDeliveryPlatformsByValueSet,
        isVideoFileName,
        defaultVideoPlatforms,
      });
      if (!sameArray(Array.isArray(currentPlatforms) ? currentPlatforms : [], nextPlatforms)) {
        uploadForm.setFieldsValue({
          available_platforms: nextPlatforms,
        });
      }
    } catch (_) {
      // best-effort
    }

    setUploadItems((prev) => {
      const next = buildUploadItemsFromFiles({ prevItems: prev || [], newFileList });
      if (next.length > 0) {
        const still = next.some((it) => it.uid === selectedUploadUid);
        if (!still) {
          setSelectedUploadUid(next[0].uid);
          const first = next[0];
          suppressUploadItemSyncRef.current = true;
          uploadItemForm.setFieldsValue(buildUploadItemFormValues(first));
          suppressUploadItemSyncRef.current = false;
        }
      } else {
        setSelectedUploadUid(null);
        uploadItemForm.resetFields();
      }
      return next;
    });
  };

  const handleRemove = (file) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);
    setUploadItems((prev) => (prev || []).filter((it) => it.uid !== file.uid));
    if (selectedUploadUid === file.uid) {
      setSelectedUploadUid(null);
      uploadItemForm.resetFields();
    }
  };

  const selectedUploadItem = useMemo(
    () => (uploadItems || []).find((it) => it.uid === selectedUploadUid) || null,
    [selectedUploadUid, uploadItems]
  );

  const selectedUploadItemSyncKey = useMemo(() => {
    if (!selectedUploadItem) return '';
    const tagsKey = Array.isArray(selectedUploadItem.tags) ? selectedUploadItem.tags.join(',') : '';
    return [
      selectedUploadItem.uid,
      selectedUploadItem.title || '',
      selectedUploadItem.summary || '',
      tagsKey,
      selectedUploadItem.text_source || '',
      String(selectedUploadItem.ocr_max_frames ?? ''),
      selectedUploadItem.aiStatus || '',
      selectedUploadItem.aiError || '',
    ].join('|');
  }, [selectedUploadItem]);

  useEffect(() => {
    if (!selectedUploadItem) return;
    suppressUploadItemSyncRef.current = true;
    uploadItemForm.setFieldsValue(buildUploadItemFormValues(selectedUploadItem));
    suppressUploadItemSyncRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUploadItemSyncKey]);

  const selectUploadItem = (uid) => {
    setSelectedUploadUid(uid);
    const it = (uploadItems || []).find((x) => x.uid === uid);
    if (it) {
      suppressUploadItemSyncRef.current = true;
      uploadItemForm.setFieldsValue(buildUploadItemFormValues(it));
      suppressUploadItemSyncRef.current = false;
    }
  };

  const syncSelectedItemFromForm = () => {
    if (!selectedUploadUid) return;
    if (suppressUploadItemSyncRef.current) return;
    const v = uploadItemForm.getFieldsValue(true) || {};
    setUploadItems((prev) => applyUploadItemFormValues({
      items: prev || [],
      selectedUploadUid,
      values: v,
    }));
  };

  const applyGlobalTextSourceToUntouched = () => {
    if (!uploadItems || uploadItems.length === 0) return;
    setUploadItems((prev) => {
      const next = applyGlobalTextSourceToItems({
        items: prev || [],
        globalTextSource,
        globalOcrMaxFrames,
      });
      const selected = next.find((it) => it.uid === selectedUploadUid);
      if (selected && !selected.text_source_customized) {
        uploadItemForm.setFieldsValue({
          text_source: selected.text_source,
          ocr_max_frames: selected.ocr_max_frames,
        });
      }
      return next;
    });
    message.success('已应用到未修改的文件');
  };

  const runPreviewForItem = async (it, applyMode = 'all') => {
    const f = it?.file?.originFileObj || it?.file;
    if (!f) throw new Error('文件缺失');
    const fd = buildPreviewMetadataFormData({ file: f, item: it });
    const resp = await previewContentMetadata(fd);
    if (resp?.status !== 'success') {
      throw new Error(resolveErrorMessage(resp?.message, 'AI生成失败'));
    }
    return mergePreviewResult({ response: resp, item: it, applyMode });
  };

  const runBatchGenerate = async ({ retryOnlyFailures = false } = {}) => {
    if (previewing || batchGenerating) return;
    const items = uploadItems || [];
    const targets = retryOnlyFailures
      ? items.filter((it) => it.aiStatus === 'error')
      : (batchSkipDone ? items.filter((it) => it.aiStatus !== 'done') : items);

    if (targets.length === 0) {
      message.info(retryOnlyFailures ? '没有需要重试的失败项' : '没有需要生成的内容');
      return;
    }

    setBatchGenerating(true);
    setBatchFailures([]);
    setBatchProgress({ total: targets.length, done: 0, ok: 0, failed: 0 });

    const targetUidSet = new Set(targets.map((t) => t.uid));
    setUploadItems((prev) =>
      (prev || []).map((it) => (targetUidSet.has(it.uid) ? { ...it, aiStatus: 'loading', aiError: '' } : it))
    );

    const limit = 3;
    let nextIndex = 0;
    let okCount = 0;
    let failedCount = 0;

    const worker = async () => {
      while (true) {
        const idx = nextIndex;
        nextIndex += 1;
        if (idx >= targets.length) return;
        const it = targets[idx];
        try {
          const patch = await runPreviewForItem(it, 'all');
          setUploadItems((prev) =>
            (prev || []).map((x) => (x.uid === it.uid ? { ...x, ...patch, aiStatus: 'done', aiError: '' } : x))
          );
          if (selectedUploadUid === it.uid) {
            uploadItemForm.setFieldsValue({
              title: patch.title,
              summary: patch.summary,
              tags: patch.tags,
            });
          }
          okCount += 1;
          setBatchProgress((p) => ({ ...p, done: p.done + 1, ok: p.ok + 1 }));
        } catch (e) {
          const errMsg = e?.message || String(e);
          setUploadItems((prev) =>
            (prev || []).map((x) => (x.uid === it.uid ? { ...x, aiStatus: 'error', aiError: errMsg } : x))
          );
          failedCount += 1;
          setBatchFailures((prev) => [...(prev || []), { uid: it.uid, name: it.name, error: errMsg }]);
          setBatchProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(limit, targets.length) }, () => worker()));
    setBatchGenerating(false);
    const total = targets.length;
    if (failedCount === 0) {
      message.success(`批量生成完成：成功 ${okCount}/${total}`);
    } else {
      message.warning(`批量生成完成：成功 ${okCount}/${total}，失败 ${failedCount}（见失败列表）`);
    }
  };

  const doPreviewForSelected = async (applyMode) => {
    if (!selectedUploadItem) return;
    const targetUid = selectedUploadItem.uid;
    const existing = uploadItemForm.getFieldsValue(true) || {};
    const f = selectedUploadItem?.file?.originFileObj || selectedUploadItem?.file;
    if (!f) return;
    try {
      setPreviewingAction(applyMode);
      setPreviewing(true);
      setUploadItems((prev) =>
        (prev || []).map((it) => (it.uid === targetUid ? { ...it, aiStatus: 'loading', aiError: '' } : it))
      );
      const fd = buildPreviewMetadataFormData({
        file: f,
        item: selectedUploadItem,
        existingValues: existing,
      });
      const resp = await previewContentMetadata(fd);
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, 'AI生成失败'));
      }
      const merged = mergePreviewResult({
        response: resp,
        existingValues: existing,
        item: selectedUploadItem,
        applyMode,
      });

      setUploadItems((prev) =>
        (prev || []).map((it) => {
          if (it.uid !== targetUid) return it;
          return {
            ...it,
            aiStatus: 'done',
            aiError: '',
            title: merged.title || it.title,
            summary: merged.summary,
            tags: merged.tags,
          };
        })
      );

      if (selectedUploadUid === targetUid) {
        uploadItemForm.setFieldsValue({ title: merged.title, summary: merged.summary, tags: merged.tags });
      }
    } catch (e) {
      setUploadItems((prev) =>
        (prev || []).map((it) =>
          it.uid === targetUid ? { ...it, aiStatus: 'error', aiError: e?.message || String(e) } : it
        )
      );
      message.error(e?.message || String(e));
    } finally {
      setPreviewing(false);
      setPreviewingAction(null);
    }
  };

  const handleUploadItemValuesChange = (changed) => {
    if (suppressUploadItemSyncRef.current) return;
    if (changed && (Object.prototype.hasOwnProperty.call(changed, 'text_source') || Object.prototype.hasOwnProperty.call(changed, 'ocr_max_frames'))) {
      setUploadItems((prev) =>
        (prev || []).map((it) =>
          it.uid === selectedUploadUid ? { ...it, text_source_customized: true } : it
        )
      );
    }
  };

  return {
    uploadForm,
    uploadItemForm,
    uploadModalVisible,
    openUploadModal,
    closeUploadModal,
    handleUpload,
    uploading,
    fileList,
    uploadItems,
    selectedUploadUid,
    selectedUploadItem,
    previewing,
    previewingAction,
    batchGenerating,
    batchProgress,
    batchFailures,
    batchSkipDone,
    setBatchSkipDone,
    globalTextSource,
    setGlobalTextSource,
    globalOcrMaxFrames,
    setGlobalOcrMaxFrames,
    handleFileChange,
    handleRemove,
    selectUploadItem,
    syncSelectedItemFromForm,
    applyGlobalTextSourceToUntouched,
    runBatchGenerate,
    doPreviewForSelected,
    handleUploadItemValuesChange,
  };
};
